import { useEffect, useMemo, useState, type ComponentType } from 'react';
import {
  Home,
  FileText,
  Shield,
  ClipboardList,
  Settings,
  FileUp,
  Library,
  Brain,
  Bell,
  User,
  BarChart2,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Building,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Workflow,
  FlaskConical,
  Sparkles,
  MonitorUp,
  Scale,
} from 'lucide-react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { useAuthStore } from '@/store/authStore';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAvatarUrl } from '@/utils/image';
import { useInvitationManagement } from '@/hooks/auth';
import { useFeatureFlags, FEATURE_FLAGS } from '@/hooks/useFeatureFlags';
import { InvitationsList } from '../auth/InvitationsList';
import { ThemeToggle } from './ThemeToggle';
import type { WorkspaceMemberResponseSerializerSchema } from '@/gen-api/models';
import { getVersionString } from '@/utils/version';
import { useRoleRosterSync } from '@/hooks/useRoleRosterSync';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'vigilant.sidebar.collapsed';

interface NavItemProps {
  to: string;
  icon: ComponentType<{ size?: number | string; className?: string }>;
  label: string;
  collapsed: boolean;
  isActive: boolean;
  onClick: () => void;
}

function NavItem({ to, icon: Icon, label, collapsed, isActive, onClick }: NavItemProps) {
  const link = (
    <Link
      to={to}
      onClick={onClick}
      className={`relative flex items-center overflow-hidden ${
        collapsed ? 'w-12 justify-center px-0' : ''
      } p-2 rounded-md hover:bg-sidebar-accent text-sidebar-foreground ${
        isActive ? 'bg-sidebar-primary text-sidebar-primary-foreground' : ''
      }`}
      aria-label={collapsed ? label : undefined}
    >
      <Icon size={20} className="shrink-0" />
      <span
        className={`absolute left-10 w-48 whitespace-nowrap transition-[opacity,transform] duration-150 ease-out ${
          collapsed ? 'opacity-0 -translate-x-2' : 'opacity-100 translate-x-0'
        }`}
        aria-hidden={collapsed}
      >
        {label}
      </span>
    </Link>
  );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

interface NavSectionHeaderProps {
  label: string;
  expanded: boolean;
  collapsed: boolean;
  onToggle: () => void;
}

function NavSectionHeader({ label, expanded, collapsed, onToggle }: NavSectionHeaderProps) {
  if (collapsed) {
    return (
      <div className="my-2 px-2">
        <div className="border-t border-sidebar-border" />
      </div>
    );
  }

  return (
    <div className="mt-6 mb-2">
      <div className="flex items-center justify-between px-2">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
        <button
          onClick={onToggle}
          className="p-1 rounded-md hover:bg-sidebar-accent text-sidebar-foreground"
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label} section`}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );
}

interface UserMenuDropdownProps {
  avatarUrl: string | null;
  fullName: string;
  currentWorkspace: WorkspaceMemberResponseSerializerSchema | null;
  workspaces: WorkspaceMemberResponseSerializerSchema[];
  onWorkspaceChange: (workspaceId: string) => void;
  invitationManagement: ReturnType<typeof useInvitationManagement>;
  collapsed: boolean;
}

function UserMenuDropdown({
  avatarUrl,
  fullName,
  currentWorkspace,
  workspaces,
  onWorkspaceChange,
  invitationManagement,
  collapsed,
}: UserMenuDropdownProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const initials =
    fullName
      .split(' ')
      .map((name) => name[0])
      .join('')
      .toUpperCase() || '??';

  const avatar = avatarUrl ? (
    <img src={avatarUrl} alt="Profile" className="h-8 w-8 rounded-full object-cover" />
  ) : (
    <div className="h-8 w-8 rounded-full bg-purple-400 bg-opacity-20 flex items-center justify-center text-purple-400 font-medium">
      {initials}
    </div>
  );

  return (
    <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="profile-menu-button"
          className={`flex items-center ${
            collapsed ? 'justify-center' : 'justify-between'
          } w-full space-x-2 hover:bg-header/80 text-header-foreground ${collapsed ? 'p-3' : 'p-4'} rounded-md`}
          aria-label={collapsed ? `Open user menu for ${fullName}` : undefined}
        >
          <div className={`flex items-center ${collapsed ? '' : 'space-x-2'}`}>
            {avatar}
            {!collapsed && (
              <div className="flex flex-col text-left">
                <span className="hover:text-white inline">{fullName}</span>
                <span className="hover:text-white inline text-xs">{currentWorkspace?.name}</span>
              </div>
            )}
          </div>
          {!collapsed && (
            <>
              {userMenuOpen ? (
                <ChevronLeft size={16} className="hover:text-white" />
              ) : (
                <ChevronRight size={16} className="hover:text-white" />
              )}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" side="right">
        <div className="p-4">
          {collapsed && (
            <div className="mb-3">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{fullName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{currentWorkspace?.name}</div>
            </div>
          )}
          {/* Workspace Selection */}
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <Building size={16} className="text-gray-500 dark:text-gray-400 mr-2" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Workspace</span>
            </div>
            <Select value={currentWorkspace?.id} onValueChange={onWorkspaceChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select workspace..." />
              </SelectTrigger>
              <SelectContent>
                {workspaces?.map((ws) => (
                  <SelectItem key={ws.id} value={ws.id} disabled={ws.role === 'member'}>
                    {ws.name}{' '}
                    {ws.role === 'member' && (
                      <span className="text-xs text-gray-500 dark:text-gray-400"> (Member)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {invitationManagement.invitations && invitationManagement.invitations.length > 0 && (
            <div className="mb-4">
              <InvitationsList
                compact
                invitations={invitationManagement.invitations}
                isLoading={invitationManagement.isLoadingInvitations}
                hasError={invitationManagement.hasInvitationsError}
                onAccept={invitationManagement.handleAcceptInvitation}
                onDecline={invitationManagement.handleDeclineInvitation}
                isAccepting={invitationManagement.isAccepting}
                isDeclining={invitationManagement.isDeclining}
              />
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-gray-700 my-2" />

          {/* Menu Items */}
          <div className="space-y-1">
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-sm text-gray-700 dark:text-gray-300">Theme</span>
              <ThemeToggle />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

            <Link
              to="/settings"
              className="flex items-center px-3 py-2 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 w-full"
              onClick={() => setUserMenuOpen(false)}
            >
              <Settings size={16} className="mr-3" />
              Settings
            </Link>

            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

            <LogoutButton
              variant="ghost"
              size="sm"
              showIcon={true}
              className="flex items-center px-3 py-2 text-sm rounded-md w-full justify-start text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function DashboardLayout() {
  useRoleRosterSync();
  const [policiesExpanded, setPoliciesExpanded] = useState(true);
  const [teammatesExpanded, setTeammatesExpanded] = useState(true);
  const [monitoringExpanded, setMonitoringExpanded] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  });
  const location = useLocation();
  const { userInfo, workspaces, currentWorkspace, setCurrentWorkspace } = useAuthStore();
  const avatarUrl = useAvatarUrl(userInfo?.avatar);

  const invitationManagement = useInvitationManagement();

  const { isFeatureEnabled } = useFeatureFlags();
  const isEvalStudioEnabled = isFeatureEnabled(FEATURE_FLAGS.EVAL_STUDIO);
  const isSkillsEnabled = isFeatureEnabled(FEATURE_FLAGS.SKILLS_RUNTIME);
  const isComputerUseEnabled = isFeatureEnabled(FEATURE_FLAGS.COMPUTER_USE);
  const isCostAnalyticsEnabled = currentWorkspace?.role === 'admin' && isFeatureEnabled(FEATURE_FLAGS.COST_ANALYTICS);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, sidebarCollapsed ? 'true' : 'false');
  }, [sidebarCollapsed]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleWorkspaceChange = (workspaceId: string) => {
    const selected = workspaces?.find((ws) => ws.id === workspaceId);
    if (selected) {
      setCurrentWorkspace(selected);
    }
    setTimeout(() => {
      // refresh the page
      window.location.reload();
    }, 100);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const fullName = useMemo(() => `${userInfo?.first_name} ${userInfo?.last_name}`, [userInfo]);

  // On mobile the drawer is always full-width; collapsed state only applies on md+
  const sidebarWidthClass = sidebarCollapsed ? 'w-[17rem] md:w-16' : 'w-[17rem]';
  const mainHeaderOffsetClass = sidebarCollapsed ? 'md:left-16' : 'md:left-[17rem]';
  const navPaddingClass = sidebarCollapsed ? 'md:p-2' : 'p-4';

  return (
    <TooltipProvider>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        {/* Mobile backdrop */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden" onClick={closeMobileMenu} />
        )}

        {/* Sidebar */}
        <div
          className={`
          fixed inset-y-0 left-0 z-50 ${sidebarWidthClass} overflow-x-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-md
          flex flex-col
          transform transition-all ${sidebarCollapsed ? 'md:duration-0' : 'duration-200'} ease-in-out
          md:relative md:translate-x-0 md:z-auto
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        >
          <div className="flex flex-col border-b border-sidebar-border bg-header flex-shrink-0">
            {/* Expanded desktop + mobile header: horizontal logo + toggle/close on the right */}
            <div
              className={`${sidebarCollapsed ? 'md:hidden' : ''} flex items-center justify-between px-2 py-2 min-h-[56px]`}
            >
              <div className="flex items-center pl-2">
                <img src="/horizontal-logo.svg" alt="Vigilant AI.ai Logo" className="h-10 w-32 mr-2" />
              </div>

              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleSidebar}
                    className="hidden md:inline-flex p-2 rounded-md hover:bg-sidebar-accent text-sidebar-foreground"
                    aria-label="Collapse sidebar"
                  >
                    <PanelLeftClose size={18} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  Collapse sidebar
                </TooltipContent>
              </Tooltip>

              {/* Close button for mobile */}
              <button onClick={closeMobileMenu} className="p-2 rounded-md hover:bg-sidebar-accent md:hidden">
                <X size={20} />
              </button>
            </div>

            {/* Collapsed desktop header: icon logo doubles as the expand toggle */}
            {sidebarCollapsed && (
              <div className="hidden md:flex items-center justify-center min-h-[56px]">
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={toggleSidebar}
                      className="group relative inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-sidebar-accent text-sidebar-foreground"
                      aria-label="Expand sidebar"
                    >
                      <img
                        src="/logo_v2.png"
                        alt="Vigilant AI Logo"
                        className="h-8 w-8 object-contain transition-opacity duration-150 group-hover:opacity-0"
                      />
                      <PanelLeftOpen
                        size={20}
                        className="absolute opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    Expand sidebar
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>

          <nav
            className={`sidebar-scroll flex flex-col flex-1 overflow-y-auto min-h-0 ${navPaddingClass} ${sidebarCollapsed ? 'p-4' : ''}`}
          >
            <ul className="space-y-2">
              {/* Dashboard */}
              <li>
                <NavItem
                  to="/dashboard"
                  icon={Home}
                  label="Dashboard"
                  collapsed={sidebarCollapsed}
                  isActive={isActive('/dashboard')}
                  onClick={closeMobileMenu}
                />
              </li>

              {/* Policies Section */}
              <li>
                <NavSectionHeader
                  label="Governance"
                  expanded={policiesExpanded}
                  collapsed={sidebarCollapsed}
                  onToggle={() => setPoliciesExpanded(!policiesExpanded)}
                />
                {(sidebarCollapsed || policiesExpanded) && (
                  <ul className={`space-y-2 ${sidebarCollapsed ? '' : 'pl-2'}`}>
                    <li>
                      <NavItem
                        to="/rules"
                        icon={Shield}
                        label="Controls & Guardrails"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/rules')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    <li>
                      <NavItem
                        to="/documents"
                        icon={FileUp}
                        label="Import Controls"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/documents')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                  </ul>
                )}
              </li>

              {/* Teammates Section */}
              <li>
                <NavSectionHeader
                  label="Teammates"
                  expanded={teammatesExpanded}
                  collapsed={sidebarCollapsed}
                  onToggle={() => setTeammatesExpanded(!teammatesExpanded)}
                />
                {(sidebarCollapsed || teammatesExpanded) && (
                  <ul className={`space-y-2 ${sidebarCollapsed ? '' : 'pl-2'}`}>
                    <li>
                      <NavItem
                        to="/teammate-studio"
                        icon={FileText}
                        label="Teammate Studio"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/teammate-studio')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    <li>
                      <NavItem
                        to="/knowledge-library"
                        icon={Library}
                        label="Knowledge Library"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/knowledge-library')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    <li>
                      <NavItem
                        to="/memory-library"
                        icon={Brain}
                        label="Memory Library"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/memory-library')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    {isSkillsEnabled && (
                      <li>
                        <NavItem
                          to="/skill-library"
                          icon={Sparkles}
                          label="Skill Library"
                          collapsed={sidebarCollapsed}
                          isActive={isActive('/skill-library')}
                          onClick={closeMobileMenu}
                        />
                      </li>
                    )}
                    <li>
                      <NavItem
                        to="/triggers"
                        icon={Workflow}
                        label="Triggers"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/triggers')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    {isEvalStudioEnabled && (
                      <li>
                        <NavItem
                          to="/eval-studio"
                          icon={FlaskConical}
                          label="Eval Studio"
                          collapsed={sidebarCollapsed}
                          isActive={isActive('/eval-studio')}
                          onClick={closeMobileMenu}
                        />
                      </li>
                    )}
                    {isComputerUseEnabled && (
                      <li>
                        <NavItem
                          to="/computer-control"
                          icon={MonitorUp}
                          label="Computer Control"
                          collapsed={sidebarCollapsed}
                          isActive={isActive('/computer-control')}
                          onClick={closeMobileMenu}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>

              {/* Monitoring Section */}
              <li>
                <NavSectionHeader
                  label="Monitoring"
                  expanded={monitoringExpanded}
                  collapsed={sidebarCollapsed}
                  onToggle={() => setMonitoringExpanded(!monitoringExpanded)}
                />
                {(sidebarCollapsed || monitoringExpanded) && (
                  <ul className={`space-y-2 ${sidebarCollapsed ? '' : 'pl-2'}`}>
                    <li>
                      <NavItem
                        to="/audit"
                        icon={ClipboardList}
                        label="Audit Logs"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/audit')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    {currentWorkspace?.slug === 'vigilantai' && (
                      <li>
                        <NavItem
                          to="/developer-logs"
                          icon={FileText}
                          label="Developer Logs"
                          collapsed={sidebarCollapsed}
                          isActive={isActive('/developer-logs')}
                          onClick={closeMobileMenu}
                        />
                      </li>
                    )}
                    {currentWorkspace?.slug === 'vigilantai' && (
                      <li>
                        <NavItem
                          to="/compliance-shadow"
                          icon={Scale}
                          label="Compliance Shadow"
                          collapsed={sidebarCollapsed}
                          isActive={isActive('/compliance-shadow')}
                          onClick={closeMobileMenu}
                        />
                      </li>
                    )}
                    <li>
                      <NavItem
                        to="/user-activity"
                        icon={User}
                        label="User Activity"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/user-activity')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    <li>
                      <NavItem
                        to="/alerts"
                        icon={Bell}
                        label="Violation Alerts"
                        collapsed={sidebarCollapsed}
                        isActive={isActive('/alerts')}
                        onClick={closeMobileMenu}
                      />
                    </li>
                    {isCostAnalyticsEnabled && (
                      <li>
                        <NavItem
                          to="/analytics"
                          icon={BarChart2}
                          label="Usage Analytics"
                          collapsed={sidebarCollapsed}
                          isActive={isActive('/analytics')}
                          onClick={closeMobileMenu}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </li>
            </ul>

            {/* Version Display */}
            {!sidebarCollapsed && (
              <div className="mt-auto px-4 py-2 text-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">{getVersionString()}</span>
              </div>
            )}
          </nav>

          <div className="flex-shrink-0 bg-sidebar">
            {/* User Menu Popover */}
            <div className="flex items-center rounded-md border-t border-sidebar-border">
              <UserMenuDropdown
                avatarUrl={avatarUrl}
                fullName={fullName}
                currentWorkspace={currentWorkspace}
                workspaces={workspaces || []}
                onWorkspaceChange={handleWorkspaceChange}
                invitationManagement={invitationManagement}
                collapsed={sidebarCollapsed}
              />
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden md:ml-0">
          {/* Global header bar */}
          <div
            className={`hidden md:block bg-header absolute top-0 ${mainHeaderOffsetClass} right-0 h-[57px] transition-all ${
              sidebarCollapsed ? 'duration-0' : 'duration-200'
            } ease-in-out`}
          ></div>
          <header className="bg-header text-header-foreground shadow-md z-20 px-4 py-2 md:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <img src="/horizontal-logo.svg" alt="Vigilant AI.ai Logo" className="h-10 w-32 mr-2" />
                </div>
                {/* Hamburger menu button for mobile */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-md hover:bg-header/80 text-header-foreground md:hidden mr-2"
                >
                  <Menu size={20} />
                </button>
              </div>
            </div>
          </header>

          <Outlet />
        </div>
      </div>
    </TooltipProvider>
  );
}
